/**
 * Version represents a semantic version.
 *
 * It is primarily used for version-matching between web- and app-side modules.
 */
export class Version<MajorVersion extends number = number> {
    /**
     * The regular expression used to parse a version string.
     *
     * Matches the following:
     *   1. Major version number (required)
     *   2. Minor version number (required)
     *   3. Patch version number (required)
     *   4. Pre-release version (optional)
     *   5. Build metadata (optional)
     */
    static PATTERN = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z-.]+))?(?:\+([0-9A-Za-z-.]+))?$/;

    constructor(
        /** The major version number. */
        readonly major: MajorVersion,
        /** The minor version number. */
        readonly minor: number,
        /** The patch version number. */
        readonly patch: number,
        /** The pre-release version. */
        readonly preRelease?: string,
        /** The build metadata. */
        readonly build?: string,
    ) {}

    /**
     * Parses a semantic version string. Does not support pre-release or build metadata.
     */
    static parse(version: Version | string): Version {
        if (version instanceof Version) {
            return version;
        }
        const match = version.match(Version.PATTERN);
        if (match) {
            const major = match[1] ? parseInt(match[1], 10) : Number.NaN; // required
            const minor = match[2] ? parseInt(match[2], 10) : Number.NaN; // required
            const patch = match[3] ? parseInt(match[3], 10) : Number.NaN; // required
            const preRelease = match[4]; // optional
            const build = match[5]; // optional
            if (!Number.isNaN(major) && !Number.isNaN(minor) && !Number.isNaN(patch)) {
                return new Version(major, minor, patch, preRelease, build);
            }
        }
        throw new Error(`Invalid version: ${version}`);
    }

    /**
     * Evaluates a function and returns the result if the major version exactly matches the provided number.
     */
    matchMajor<T, RequiredMajorVersion extends number>(
        major: RequiredMajorVersion,
        fn: (version: Version<RequiredMajorVersion>) => T,
    ): T | undefined {
        if ((this.major as number) === (major as number)) {
            return fn(this as unknown as Version<RequiredMajorVersion>);
        }
    }

    /**
     * Evaluates a function and returns the result if the minor version is the same or higher than the provider number.
     */
    matchMinor<T>(minor: number, fn: (version: Version<MajorVersion>) => T): T | undefined {
        if (this.minor >= minor) {
            return fn(this as unknown as Version<MajorVersion>);
        }
    }

    /**
     * Evaluates a function and returns the result if the major version exactly matches and the minor version is the same
     * or higher than the provided versions.
     */
    matchMajorMinor<T, RequiredMajorVersion extends number>(
        major: RequiredMajorVersion,
        minor: number,
        fn: (version: Version<RequiredMajorVersion>) => T,
    ): T | undefined {
        return this.matchMajor(major, (version) => version.matchMinor(minor, fn));
    }

    /**
     * Returns a string representation of this version.
     */
    toString(): string {
        let output = `${this.major}.${this.minor}.${this.patch}`;
        if (this.preRelease !== undefined) {
            output += `-${this.preRelease}`;
        }
        if (this.build !== undefined) {
            output += `+${this.build}`;
        }
        return output;
    }

    /**
     * Returns true if this version is equal to the provided version.
     */
    equals(other: Version | string): boolean {
        other = Version.parse(other);
        return (
            this.major === other.major &&
            this.minor === other.minor &&
            this.patch === other.patch &&
            this.preRelease === other.preRelease
        );
    }

    /**
     * Returns true if this version is greater than the provided version.
     *
     * Reference: https://semver.org/#spec-item-11
     */
    isGreaterThan(other: Version | string): boolean {
        // Precedence refers to how versions are compared to each other when ordered.
        // Precedence MUST be calculated by separating the version into major, minor, patch and pre-release identifiers in that order (Build metadata does not figure into precedence).
        other = Version.parse(other);

        // Precedence is determined by the first difference when comparing each of these identifiers from left to right as follows: Major, minor, and patch versions are always compared numerically.
        // Example: 1.0.0 < 2.0.0 < 2.1.0 < 2.1.1.
        if (this.major > other.major) {
            return true;
        }
        if (this.major === other.major) {
            if (this.minor > other.minor) {
                return true;
            }
            if (this.minor === other.minor) {
                if (this.patch > other.patch) {
                    return true;
                }
                if (this.patch === other.patch) {
                    // When major, minor, and patch are equal, a pre-release version has lower precedence than a normal version:
                    // Example: 1.0.0-alpha < 1.0.0.
                    if (this.preRelease === undefined && other.preRelease === undefined) {
                        return false;
                    }
                    if (this.preRelease === undefined) {
                        return true;
                    }
                    if (other.preRelease === undefined) {
                        return false;
                    }

                    // Precedence for two pre-release versions with the same major, minor, and patch version MUST be determined by comparing each dot separated identifier from left to right until a difference is found as follows:
                    // Identifiers consisting of only digits are compared numerically.
                    // Identifiers with letters or hyphens are compared lexically in ASCII sort order.
                    // Numeric identifiers always have lower precedence than non-numeric identifiers.
                    // A larger set of pre-release fields has a higher precedence than a smaller set, if all of the preceding identifiers are equal.
                    // Example: 1.0.0-alpha < 1.0.0-alpha.1 < 1.0.0-alpha.beta < 1.0.0-beta < 1.0.0-beta.2 < 1.0.0-beta.11 < 1.0.0-rc.1 < 1.0.0.
                    const thisPreRelease = this.preRelease.split('.');
                    const otherPreRelease = other.preRelease.split('.');

                    for (let i = 0; i < Math.max(thisPreRelease.length, otherPreRelease.length); i++) {
                        const thisPreReleasePart = thisPreRelease[i];
                        const otherPreReleasePart = otherPreRelease[i];
                        if (thisPreReleasePart === undefined) {
                            return false;
                        }
                        if (otherPreReleasePart === undefined) {
                            return true;
                        }
                        if (thisPreReleasePart === otherPreReleasePart) {
                            continue;
                        }
                        const thisPreReleasePartIsNumber = /^\d+$/.test(thisPreReleasePart);
                        const otherPreReleasePartIsNumber = /^\d+$/.test(otherPreReleasePart);
                        if (thisPreReleasePartIsNumber && otherPreReleasePartIsNumber) {
                            return parseInt(thisPreReleasePart, 10) > parseInt(otherPreReleasePart, 10);
                        }
                        if (thisPreReleasePartIsNumber) {
                            return false;
                        }
                        if (otherPreReleasePartIsNumber) {
                            return true;
                        }
                        return thisPreReleasePart > otherPreReleasePart;
                    }
                }
            }
        }
        return false;
    }

    /**
     * Returns true if this version is greater than or equal to the provided version.
     */
    isGreaterThanOrEqual(other: Version | string): boolean {
        other = Version.parse(other);
        return this.equals(other) || this.isGreaterThan(other);
    }

    /**
     * Returns true if this version is less than the provided version.
     */
    isLessThan(other: Version | string): boolean {
        return !this.isGreaterThanOrEqual(other);
    }

    /**
     * Returns true if this version is less than or equal to the provided version.
     */
    isLessThanOrEqual(other: Version | string): boolean {
        other = Version.parse(other);
        return this.equals(other) || !this.isGreaterThan(other);
    }
}
